import React, { useState } from 'react';
import { useFadeIn } from '../hooks/useFadeIn';

const VALUES = [
  'Integrity',
  'Community Impact',
  'Sustainability',
  'Quality & Excellence',
  'Innovation',
  'Integrity & Transparency',
  'Responsibility & Stewardship',
  'Partnership & Growth',
];

export default function About({ dark }) {
  const ref = useFadeIn();
  const [videoLoaded, setVideoLoaded] = useState(false);

  return (
    <section id="about" className={`py-24 px-6 ${dark ? 'bg-green-900/20' : 'bg-green-50/60'}`}>
      <div className="max-w-6xl mx-auto" ref={ref}>

        {/* Top row: Mission/Vision + Text */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center mb-16">

          {/* Left: Mission/Vision card */}
          <div className="fade-in">
            <div className={`rounded-2xl p-10 relative overflow-hidden ${dark ? 'bg-[#1a3a1e]' : 'bg-green-800'}`}>
              <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-green-400/8" />
              <div className="absolute -bottom-8 -left-8 w-24 h-24 rounded-full bg-green-400/5" />
              {[
                { label: 'Our Mission', text: 'To produce safe, nutritious, and high-quality agricultural products through modern greenhouse and open-field farming practices, driven by sustainability, innovation, and operational excellence.' },
                { label: 'Our Vision',  text: 'To become a leading agricultural enterprise recognized for excellence in sustainable greenhouse farming, innovation, and the consistent delivery of high-quality farm produce.' },
              ].map(item => (
                <div key={item.label} className="relative z-10 bg-white/6 border border-white/12 rounded-xl p-5 mb-4 last:mb-0">
                  <p className="text-xs font-semibold text-green-400 tracking-widest uppercase mb-2">{item.label}</p>
                  <p className="text-sm text-green-200/80 leading-relaxed italic">"{item.text}"</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Text + Values */}
          <div className="fade-in">
            <span className={`inline-block text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-5
              ${dark ? 'bg-green-400/10 text-green-400' : 'bg-green-100 text-green-800'}`}>
              Who We Are
            </span>
            <h2 className={`font-serif text-3xl md:text-4xl font-bold leading-tight mb-6 ${dark ? 'text-white' : 'text-green-900'}`}>
              Global Exclusive Ariel Farm Ltd
            </h2>
            <p className={`text-base leading-relaxed mb-8 ${dark ? 'text-green-300/70' : 'text-green-700/80'}`}>
              A professionally managed agricultural enterprise committed to sustainable food production, agricultural innovation, and long-term value creation. We specialize in modern greenhouse farming alongside open-field cultivation — ensuring consistent, high-quality produce all year round.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {VALUES.map(v => (
                <div key={v}
                  className={`flex items-center gap-2.5 border rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200 cursor-default
                    ${dark
                      ? 'bg-green-900/30 border-green-400/10 text-green-300 hover:border-green-400/30 hover:bg-green-400/5'
                      : 'bg-white border-green-800/12 text-green-800 hover:border-green-600 hover:bg-green-50 hover:translate-x-1'}`}
                >
                  <span className="w-2 h-2 rounded-full bg-green-600 flex-shrink-0" />
                  {v}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Facebook Video Embed */}
        <div className="fade-in">
          <div className={`rounded-2xl overflow-hidden border ${dark ? 'border-green-400/10 bg-[#1a3a1e]' : 'border-green-200 bg-white'} p-4 md:p-6`}>
            <p className={`text-xs font-semibold tracking-widest uppercase mb-4 ${dark ? 'text-green-400' : 'text-green-700'}`}>
              See Us In Action
            </p>

            {/* Responsive 16:9 container */}
            <div className="relative w-full rounded-xl overflow-hidden" style={{ paddingBottom: '56.25%' }}>

              {/* Skeleton loader — visible until iframe loads */}
              {!videoLoaded && (
                <div className={`absolute inset-0 flex flex-col items-center justify-center gap-4 rounded-xl
                  ${dark ? 'bg-green-900/40' : 'bg-green-50'}`}>
                  {/* Animated pulse bars */}
                  <div className="w-full h-full absolute inset-0 overflow-hidden rounded-xl">
                    <div className={`w-full h-full animate-pulse ${dark ? 'bg-green-800/60' : 'bg-green-100'}`} />
                  </div>
                  {/* Play icon + label */}
                  <div className="relative z-10 flex flex-col items-center gap-3">
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center
                      ${dark ? 'bg-green-700/60' : 'bg-green-200'}`}>
                      {/* Spinning loader ring */}
                      <svg className="animate-spin w-7 h-7 text-green-500" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.2" />
                        <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </div>
                    <p className={`text-xs font-medium tracking-wide ${dark ? 'text-green-400' : 'text-green-600'}`}>
                      Loading video…
                    </p>
                  </div>
                </div>
              )}

              {/* Iframe — hidden until loaded, then fades in */}
              <iframe
                src="https://www.facebook.com/plugins/video.php?height=316&href=https%3A%2F%2Fweb.facebook.com%2F61553238297564%2Fvideos%2F1256986388737315%2F&show_text=false&width=560&t=0"
                className={`absolute inset-0 w-full h-full rounded-xl transition-opacity duration-700
                  ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
                style={{ border: 'none', overflow: 'hidden' }}
                scrolling="no"
                frameBorder="0"
                allowFullScreen
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                onLoad={() => setVideoLoaded(true)}
              />
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}